import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  ProductsStockForAdminViewPagination,
  downloadProductStockReportForAdmin,
  fetchProductsCostByWarehouse,
} from "../../store/slices/productSlice";
import debounce from "lodash/debounce";
import {
  Menu,
  Transition,
  MenuButton,
  MenuItems,
  MenuItem,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";

interface WarehouseStock {
  warehouse: { _id: string; name: string };
  quantity: number;
  totalPrice: number;
  totalPurchasedQuantity: number;
  totalPurchasedPrice: number;
}

interface ProductStock {
  _id: string;
  productName: string;
  warehouseStock: WarehouseStock[];
}

interface WarehouseValue {
  warehouseId: string;
  name: string;
  totalPrice: number;
  totalPurchasedPrice: number;
}

const formatValue = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return "0";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace("INR", "₹");
};

interface Props {
  globalDate: string;
}

const ProductStockPageByWarehouse: React.FC<Props> = ({ globalDate }) => {
  const dispatch = useDispatch<RootDispatch>();

  const {
    warehouseProducts,
    warehouseLoading,
    totalWarehousePages,
    currentWarehousePage,
    warehouseLimit,
    totalWarehouseItems,
    downloadLoading,
    costByWarehouse,
    costLoading,
  } = useSelector((state: RootState) => state.product);

  const grandTotalCurrent = costByWarehouse?.overallTotal?.totalPrice || 0;

  const warehouseValues = Object.entries(
    costByWarehouse?.warehouseWiseTotal || {},
  ).map(([name, data]: [string, any]) => ({
    warehouseId: name,
    name,
    totalPrice: data.totalPrice || 0,
    totalPurchasedPrice: data.totalPurchasedPrice || 0,
  }));

  const [search, setSearch] = useState("");
  const [localPage, setLocalPage] = useState(currentWarehousePage || 1);

  const fetchData = useCallback(
    (page: number, searchTerm: string) => {
      if (warehouseLimit) {
        dispatch(
          ProductsStockForAdminViewPagination({
            page,
            limit: warehouseLimit,
            search: searchTerm,
          }),
        );
      }
    },
    [dispatch, warehouseLimit],
  );

  useEffect(() => {
    dispatch(fetchProductsCostByWarehouse());
  }, [dispatch]);

  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        setLocalPage(1);
        fetchData(1, searchValue);
      }, 800),
    [fetchData],
  );

  useEffect(() => {
    fetchData(1, "");
  }, []);

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  useEffect(() => {
    if (currentWarehousePage && currentWarehousePage !== localPage) {
      setLocalPage(currentWarehousePage);
    }
  }, [currentWarehousePage, localPage]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    debouncedSearch(value);
  };

  const handlePageChange = (newPage: number) => {
    fetchData(newPage, search);
  };

  const noResultsMessage = useMemo(
    () =>
      search
        ? {
            title: "No products found",
            subtitle: "Try a different search term or clear the search box.",
          }
        : {
            title: "No stock available",
            subtitle: "Stock will appear here once products are added.",
          },
    [search],
  );

  const totalStockForProduct = (product: ProductStock) =>
    (product.warehouseStock || []).reduce(
      (sum, w) => sum + (w.quantity || 0),
      0,
    );

  const downloadStockReport = (format: "xlsx" | "csv") => {
    if (!downloadLoading) {
      dispatch(
        downloadProductStockReportForAdmin({
          format,
          search,
          date: globalDate,
        }),
      );
    }
  };

  const productsWithCost = useMemo(() => {
    return (warehouseProducts || []).map((product) => {
      const costData = costByWarehouse?.products?.find(
        (item: any) => item.productId === product._id,
      );

      const newWarehouseStock: WarehouseStock[] = (
        product.warehouseStock || []
      ).map((stockItem) => {
        const warehouseName = stockItem.warehouse?.name;

        const costEntry =
          warehouseName && costData?.warehouses
            ? costData.warehouses[warehouseName]
            : null;

        return {
          ...stockItem,
          totalPurchasedQuantity: stockItem.totalPurchasedQuantity || 0,
          totalPrice: costEntry?.totalPrice || 0,
          totalPurchasedPrice: costEntry?.totalPurchasedPrice || 0,
        };
      });

      return {
        ...product,
        warehouseStock: newWarehouseStock,
      };
    });
  }, [warehouseProducts, costByWarehouse]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 w-full">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6">
        <div className="bg-blue-50/50 px-6 py-3 border-b border-gray-100 rounded-t-2xl flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-800 flex items-center">
            <svg
              className="w-5 h-5 mr-2 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-1.5-1.5M4 7l1.5-1.5M20 17l-1.5 1.5M4 17l1.5 1.5M3 12h18M9 3v18m6-18v18"
              />
            </svg>
            Finished Goods & Price Overview
          </h3>

          <div className="flex items-center text-sm sm:text-lg font-semibold text-gray-700">
            <span className="text-gray-600 mr-2">Total Price:</span>

            <div className="flex items-center">
              {costLoading || warehouseLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-1.5"></div>
                  <span className="text-blue-600 text-base">Loading...</span>
                </div>
              ) : (
                <span className="text-blue-700 bg-blue-100 px-3 py-1 rounded-full shadow-inner text-base">
                  {formatValue(grandTotalCurrent)}
                </span>
              )}

              {!(costLoading || warehouseLoading) && (
                <Menu
                  as="div"
                  className="relative inline-block text-left ml-2 z-20"
                >
                  <MenuButton
                    className="flex items-center justify-center h-8 w-8 rounded-full text-blue-600 hover:bg-blue-200 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="View warehouse wise Value breakdown"
                  >
                    <InformationCircleIcon
                      className="w-5 h-5"
                      aria-hidden="true"
                    />
                  </MenuButton>

                  <Transition
                    as={React.Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <MenuItems className="absolute right-0 z-20 mt-2 w-72 origin-top-right rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none p-3 border border-gray-100">
                      <div className="py-1 max-h-96 overflow-y-auto">
                        <h4 className="text-sm font-bold text-gray-700 px-3 pb-2 border-b border-gray-100 mb-2">
                          Price by Warehouse
                        </h4>

                        {(warehouseValues as WarehouseValue[]).map(
                          (warehouse) => (
                            <div
                              key={warehouse.warehouseId}
                              className="flex justify-between items-start px-3 py-2 text-sm hover:bg-gray-50 rounded-lg transition-colors border-b border-gray-50 last:border-0"
                            >
                              <span className="text-gray-600 font-medium">
                                {warehouse.name}
                              </span>
                              <div className="text-right">
                                <div className="font-bold text-gray-800">
                                  {formatValue(warehouse.totalPrice)}
                                </div>
                                <div className="text-xs text-blue-500">
                                  (Purchased:{" "}
                                  {formatValue(warehouse.totalPurchasedPrice)})
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </MenuItems>
                  </Transition>
                </Menu>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 flex justify-between items-center">
          <label htmlFor="product-search" className="sr-only">
            Search Product Stock
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              id="product-search"
              type="text"
              placeholder="Search by product name..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full max-w-sm p-2 pl-8 border border-slate-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-all text-sm shadow-sm"
            />
          </div>

          <Menu as="div" className="relative inline-block text-left">
            <div>
              <MenuButton
                className={`inline-flex justify-center items-center rounded-lg border shadow-sm px-4 py-2 text-sm font-medium transition-all ${
                  downloadLoading
                    ? "bg-gray-200 text-gray-500 cursor-wait border-gray-300"
                    : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                }`}
                disabled={downloadLoading}
              >
                {downloadLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon
                      className="w-4 h-4 mr-2"
                      aria-hidden="true"
                    />
                    Download Report
                    <ChevronDownIcon
                      className="w-5 h-5 ml-2 -mr-1"
                      aria-hidden="true"
                    />
                  </>
                )}
              </MenuButton>
            </div>

            <Transition
              as={React.Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={() => downloadStockReport("xlsx")}
                        disabled={downloadLoading}
                        className={`${
                          active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                        } block w-full text-left px-4 py-2 text-sm ${
                          downloadLoading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        Download XLSX
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={() => downloadStockReport("csv")}
                        disabled={downloadLoading}
                        className={`${
                          active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                        } block w-full text-left px-4 py-2 text-sm ${
                          downloadLoading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        Download CSV
                      </button>
                    )}
                  </MenuItem>
                </div>
              </MenuItems>
            </Transition>
          </Menu>
        </div>
      </div>

      {(warehouseLoading || costLoading) && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 p-3 mb-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-600 mr-2"></div>
            <span className="text-sm font-medium">
              Loading product stock and cost data...
            </span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-100 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <th className="px-4 py-2 text-left w-1/5">Product Name</th>
                <th className="px-4 py-2 w-[100px]">Total Stock</th>
                <th className="px-4 py-2 w-[120px]">Total Price</th>
                <th className="px-4 py-2 w-1/3">Quantity by Warehouse</th>
                <th className="px-4 py-2 w-1/3">Price by Warehouse</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {productsWithCost.length === 0 &&
              !warehouseLoading &&
              !costLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <svg
                        className="w-12 h-12 mb-3 text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-8m-8 0H4"
                        />
                      </svg>
                      <p className="text-base font-semibold text-gray-500">
                        {noResultsMessage.title}
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        {noResultsMessage.subtitle}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                (productsWithCost as ProductStock[]).map((p) => {
                  const totalStock = totalStockForProduct(p);
                  const totalPrice = (p.warehouseStock || []).reduce(
                    (sum, w) => sum + (w.totalPrice || 0),
                    0,
                  );

                  return (
                    <tr
                      key={p._id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                        {p.productName}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span className="inline-flex items-center justify-center bg-green-100 text-green-800 text-base font-bold px-2 py-1 rounded-lg min-w-[5rem]">
                          {totalStock.toFixed(0)}
                        </span>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span className="inline-flex items-center justify-center bg-purple-100 text-purple-800 text-base font-bold px-2 py-1 rounded-lg min-w-[5rem]">
                          {formatValue(totalPrice)}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 justify-center">
                          {(p.warehouseStock || []).map((w) => (
                            <span
                              key={w.warehouse._id}
                              className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                              title={`${w.warehouse.name} – In-House: ${w.quantity}, Purchased: ${w.totalPurchasedQuantity}`}
                            >
                              {w.warehouse.name || "Unknown"}:
                              <strong className="ml-1 text-blue-600">
                                {w.quantity.toFixed(0)}
                              </strong>
                              {w.totalPurchasedQuantity > 0 && (
                                <span className="ml-1 text-gray-700 font-medium">
                                  (Purchased:{" "}
                                  <span className="text-blue-600 font-bold">
                                    {w.totalPurchasedQuantity}
                                  </span>
                                  )
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 justify-center">
                          {(p.warehouseStock || []).map((w) => (
                            <span
                              key={`${w.warehouse._id}-price`}
                              className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                              title={`${w.warehouse.name} Price: ${formatValue(
                                w.totalPrice,
                              )}`}
                            >
                              {w.warehouse.name || "Unknown"}:
                              <strong className="ml-1 text-purple-600 font-bold">
                                {formatValue(w.totalPrice)}
                              </strong>
                              {w.totalPurchasedPrice > 0 && (
                                <span className="ml-1 text-gray-700 font-medium">
                                  (Purchased:{" "}
                                  <span className="text-purple-600 font-bold">
                                    {formatValue(w.totalPurchasedPrice)}
                                  </span>
                                  )
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalWarehousePages > 1 && !warehouseLoading && (
          <div className="p-3 flex justify-between items-center border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <p className="text-xs text-gray-600">
              Showing page <strong>{currentWarehousePage}</strong> of{" "}
              <strong>{totalWarehousePages}</strong> (
              <strong>{totalWarehouseItems ?? 0}</strong> total items)
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(localPage - 1)}
                disabled={localPage <= 1}
                className={`flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  localPage <= 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-blue-600 border-blue-300 hover:bg-blue-50"
                }`}
              >
                Previous
              </button>

              <button
                onClick={() => handlePageChange(localPage + 1)}
                disabled={localPage >= totalWarehousePages}
                className={`flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  localPage >= totalWarehousePages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-blue-600 border-blue-300 hover:bg-blue-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductStockPageByWarehouse;
